import React, { useMemo, useState, useEffect } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/solid";
import { useDebounce } from "use-debounce";
import { useDispatch, useSelector } from "react-redux";

import Table from "components/lib/Global/ReactTable";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { delay, getFullName } from "utils";
import CompanyOfficialSubRow from "./CompanyOfficialSubRow";
import SearchInput from "components/lib/Shared/SearchInput";
import { getCompanyOfficialsAsync } from "state/slices/companyDashboard";
import AvatarIcon from "components/lib/Shared/Icons/AvatarIcon";
import { CORPORATES } from "components/PLCTABS/SFLdata/config/constants";
import AppointmentIcon from "components/lib/Shared/Icons/AppointmentIcon";
import { Constants } from "config/constants";
import ChangeOfParticularsIcon from "components/lib/Shared/Icons/ChangeOfParticularsIcon";
import CessationIcon from "components/lib/Shared/Icons/CessationIcon";

function StakeholderTable({
  companyId,
  isInChangesModule,
  data,
  dateFrom,
  dateTo,
  isHistory,
  totalCount,
  currentTab,
  currentSubTab,
  isLoading,
  shareAllotments,
  latestVersion,
  previousVersion,
  goToPrevStep = () => null,
}) {
  const [expandedRow, setExpandedRow] = useState({});
  const [initialRows, setInitialRows] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [changeOfparticularsMarker, setChangeOfparticularsMarker] =
    useState(false);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);
  const dispatch = useDispatch();

  const companyDashboardSlice = useSelector(
    (state) => state.historyCompanyDashboardSlice
  );

  const { getCompanyOfficialsStatus } = companyDashboardSlice;

  const transformSharesAllotted = (sharesAllotted) => {
    const grouped = sharesAllotted.reduce(
      (acc, { classOfShares, allottedShares }) => {
        acc[classOfShares] = (acc[classOfShares] || 0) + allottedShares;
        return acc;
      },
      {}
    );

    const html = Object.entries(grouped)
      .map(
        ([classOfShares, totalAllotted]) =>
          `<div>${classOfShares}: ${totalAllotted}</div>`
      )
      .join("");

    return html;
  };

  // Merge data with shareAllotments when in changes module
  const tableData = useMemo(() => {
    if (isInChangesModule && shareAllotments?.length > 0) {
      // Flatten shareAllotments.shareholders and merge with data
      const mergedData = data.map((shareholder) => {
        // Find matching shareholder in shareAllotments
        const matchingShareholder = shareAllotments
          .flatMap((allotment) => allotment.shareholders)
          .find((sh) => sh.proposed._id === shareholder._id);

        if (matchingShareholder) {
          const { proposed } = matchingShareholder;
          const allotment = shareAllotments.find((allotment) =>
            allotment.shareholders.includes(matchingShareholder)
          );
          return {
            ...shareholder,
            sharesAllotted:
              proposed.sharesAllotted || shareholder.sharesAllotted || [],
            allottedShares: proposed.allottedShares || 0,
            additionalSharesAllotted: proposed.additionalSharesAllotted || 0,
            classOfShares: allotment.classOfShares || "N/A",
            offerPricePerShare: allotment.offerPricePerShare || "N/A",
            premiumPerShare: allotment.premiumPerShare || "N/A",
            dateOfOffer: allotment.dateOfOffer || "N/A",
          };
        }
        return shareholder;
      });
      return mergedData;
    }
    return data;
  }, [isInChangesModule, shareAllotments, data]);

  const columns = useMemo(() => {
    const tempTabs = [
      {
        Header: () => "Full Name",
        id: "Name",
        accessor: "fullName",
        Cell: ({ row: { original } }) => {
          const { names, fileUploads } = original;
          const fullName = getFullName(names, false) || "N/A";
          if (
            CORPORATES.includes(original.idType) ||
            (original.designation === "Joint Shareholder" &&
              original.isDirector !== "Yes")
          ) {
            return <h1>{fullName}</h1>;
          }
          return (
            <div>
              <div className="flex items-center space-x-1">
                <div className={`flex-shrink-0`}>
                  {fileUploads?.passportPhoto ? (
                    <img
                      className="w-8 h-8 rounded-full"
                      src={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/${fileUploads?.passportPhoto}`}
                      alt={fullName}
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full">
                      <div className="relative w-8 h-8 overflow-hidden bg-gray-100 rounded-full dark:bg-gray-600">
                        <AvatarIcon />
                      </div>
                    </div>
                  )}
                </div>
                <div className="px-3">
                  <h1 className="">{fullName}</h1>
                </div>
              </div>
              <div>
                {original.designation === "Alternate Director" ? (
                  <p className="text-xs">
                    Alternate Director to{" "}
                    {original.appointers
                      .map((item) => getFullName(item?.names) || "N/A")
                      .join(", ")}
                  </p>
                ) : null}
              </div>
            </div>
          );
        },
        SubCell: (cellProps) => <>{cellProps.value}</>,
      },
      {
        Header: () => "ID Type",
        id: "ID Type",
        accessor: "idType",
      },
      {
        Header: () => "Action",
        id: "expander",
        Cell: ({ row, ...rest }) => {
          const originalRow = row.original;
          let handleViewRow = async () => {
            const shouldExpand = !row.isExpanded;
            row.toggleAllRowsExpanded(false);
            await delay(10);
            setInitialRows(rest.initialRows);
            setExpandedRow(shouldExpand ? row : null);
            if (shouldExpand) {
              row.getToggleRowExpandedProps().onClick();
            }
          };
          if (
            !originalRow?.linkToCompany &&
            !originalRow?.shareholderWithBO &&
            currentTab === "beneficialOwnershipTab"
          ) {
            handleViewRow = async () => {
              goToPrevStep();
              await delay();
              let companyShareholderEditBtn = null;
              if (originalRow?.JSGroupID)
                companyShareholderEditBtn = document.querySelector(
                  `[id="${originalRow?.JSGroupID}"]`
                );
              else if (originalRow?._id && !originalRow.JSGroupID)
                companyShareholderEditBtn = document.querySelector(
                  `[id="${originalRow?._id}"]`
                );
              if (companyShareholderEditBtn) {
                companyShareholderEditBtn.click();
              }
            };
          }

          return (
            <div className="flex">
              <span
                className="text-sm font-normal text-primary"
                {...row.getToggleRowExpandedProps()}
                onClick={handleViewRow}
                id={
                  !originalRow.linkToCompany &&
                    currentTab === "beneficialOwnershipTab"
                    ? null
                    : originalRow._id
                }
              >
                {row.isExpanded ? (
                  <ChevronUpIcon className="w-6 h-6" />
                ) : (
                  <ChevronDownIcon className="w-6 h-6" />
                )}
              </span>
              {isHistory &&
                (originalRow.appointmentMarker ||
                  originalRow.cessationMarker ||
                  originalRow.changeMarker) && (
                  <>
                    {originalRow.appointmentMarker && (
                      <span className="text-sm font-normal">
                        <AppointmentIcon />
                      </span>
                    )}
                    {originalRow.changeMarker && (
                      <span className="text-sm font-normal">
                        <ChangeOfParticularsIcon />
                      </span>
                    )}
                    {originalRow.cessationMarker && (
                      <span className="text-sm font-normal">
                        <CessationIcon />
                      </span>
                    )}
                  </>
                )}
            </div>
          );
        },
        SubCell: () => null,
      },
    ];
    if (currentTab === "stakeholderTab") {
      tempTabs.splice(
        2,
        0,
        {
          Header: () => "Designation",
          id: "Designation",
          Cell: ({ row: { original } }) => {
            return Array.isArray(original.fullName)
              ? "Joint Shareholder"
              : original.isDirector === "Yes"
                ? "Director"
                : original.designation ?? "";
          },
        },
        {
          Header: () => "Shares Allotted",
          id: "Shares Allotted",
          Cell: ({ row }) => {
            if (
              row.original?.isDirector === "Yes" &&
              !Array.isArray(row.original?.fullName)
            )
              return "";
            const sharesAllotted = row.original?.sharesAllotted ?? [];
            return (
              <span
                className="space-y-1"
                dangerouslySetInnerHTML={{
                  __html: transformSharesAllotted(sharesAllotted),
                }}
              />
            );
          },
        }
      );
    }
    return tempTabs;
  }, [currentTab, dateFrom, dateTo]);

  const renderRowSubComponent = useMemo(
    () =>
      ({ row, rowProps, visibleColumns }) =>
      (
        <CompanyOfficialSubRow
          tableData={tableData}
          row={row}
          isHistory={isHistory}
          // dateFrom={dateFrom}
          // dateTo={dateTo}
          isInChangesModule={isInChangesModule}
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          currentTab={currentTab}
          initialRows={initialRows}
          latestVersion={latestVersion}
          previousVersion={previousVersion}
          setChangeOfparticularsMarker={setChangeOfparticularsMarker}
        />
      ),
    [initialRows, currentTab]
  );

  useEffect(() => {
    dispatch(
      getCompanyOfficialsAsync({
        companyId,
        queryParams: {
          search: debouncedSearchTerm.trim(),
          limit: rowsPerPage,
          skip: (currentPage - 1) * rowsPerPage,
          order: "desc",
          isDashboard: true,
        },
      })
    );
  }, [companyId, currentPage, debouncedSearchTerm, rowsPerPage, dispatch]);

  useEffect(() => {
    if (expandedRow?.isExpanded) expandedRow?.toggleRowExpanded();
  }, [currentTab, currentSubTab, expandedRow]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  // Client-side filtering as a fallback
  const filteredData = useMemo(() => {
    if (!debouncedSearchTerm) return tableData;
    return tableData.filter((item) =>
      getFullName(item.names)
        ?.toLowerCase()
        .includes(debouncedSearchTerm.toLowerCase())
    );
  }, [tableData, debouncedSearchTerm]);

  return (
    <>
      <div className="flex items-center justify-between my-2">
        <div className="flex items-center space-x-4">
          <h2 className="font-semibold">
            {isInChangesModule ? "Current Shareholders" : "Filter by:"}
          </h2>
        </div>
        <SearchInput
          value={searchTerm}
          handleOnChange={handleSearch}
          placeholder="Search by name"
        />
      </div>
      <div className="w-full overflow-x-auto">
        {isLoading && !data?.length ? (
          <div className="flex items-center justify-center mt-10">
            <SpinnerIcon className="text-gray-400" />
          </div>
        ) : filteredData.length > 0 ? (
          <Table
            columns={columns}
            data={filteredData}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            totalRows={totalCount}
            setCurrentPage={setCurrentPage}
            paginate
            dataRowCellStyle="whitespace-normal"
            renderRowSubComponent={renderRowSubComponent}
            isLoading={getCompanyOfficialsStatus.status === "loading"}
          />
        ) : (
          <p className="text-md text-center text-sflPrimary mt-10">
            No stakeholders found
          </p>
        )}
      </div>
    </>
  );
}

export default StakeholderTable;
